import { useState, useEffect, useRef } from "react";
import cn from "classnames";
import API from "../../../functions/api";
import Button from "../../../audi-ui-components/Button";
// import Select from '../../audi-ui-components/Select';
import TextField from "../../../audi-ui-components/TextField";
import Modal from "../../../audi-ui-components/Modal";
import RangeSlider from "../../../components/RangeSlider";
import { formatNumber, formatDollar, unFormatNumber } from "../../../functions/textFormatting";
import { gtmPush } from "../../../functions/gtm";
import _debounce from "lodash/debounce";

const BudgetCalculatorComponent = ({
  amount,
  locPostcode,
  onChangePostcode,
  onCalculateBudget,
  onReset = ()=>{},
  offer,
  className,
  disclaimer,
  theme,
}) => {

  const auiTheme = theme === "dark" ? "light" : null;
  const defaultConsumerRate = 0.1089;
  const defaultBusinessRate = 0.0823;
  const initStates = {
    deposit: 1000,
    term: 60,
    balloon: 0.3,
  };

  const [products, setProducts] = useState([]);
  
  const [rate, setRate] = useState(defaultConsumerRate);
  const [deposit, setDeposit] = useState(initStates.deposit);
  const [term, setTerm] = useState(initStates.term);
  const [balloon, setBalloon] = useState(initStates.balloon);
  const [product, setProduct] = useState("ConsumerLoan");
  const [hasTrackedProduct, setHasTrackedProduct] = useState(false);
  const [depositHasFocus, setDepositHasFocus] = useState(false);
  const [weeklyBudget, setWeeklyBudget] = useState("");
  
  const [showDisclaimers, setShowDisclaimers] = useState(false);
  const [hasTrackedDisclaimers, setHasTrackedDisclaimers] = useState(false);

  const debouncedGtmPush = useRef(_debounce((category, action, label, data) => { gtmPush(category, action, label, data); }, 800));

  useEffect(() => {
    // get loan products
    API.get(
      `/1/afs/products`
    ).then((response) => {
      // console.log(response);
      if (response.data?.Products) {
        setProducts(response.data.Products.Product);
        // setProduct(response.data.Products.Default);
      }
      setLoading(false);
    }).catch((error) => {
      // console.error(error);
      // setError("Could not load product data.");
      // setLoading(false);
    });
    // eslint-disable-next-line
  }, []);
  
  useEffect(()=>{
    if (typeof onCalculateBudget === "function") {
      if (weeklyBudget && product !== null) {
        onCalculateBudget({
          interestRate: (rate * 100).toFixed(2),
          purpose: product,
          deposit: deposit,
          term: term,
          balloonRate: balloon,
          desiredWeeklyPayments: weeklyBudget
        });
      } else {
        onCalculateBudget(null);
      }
    }
    // eslint-disable-next-line
  }, [amount, rate, deposit, term, balloon, product, weeklyBudget]);
  
  return (
    <div className={cn("finance-calc-component", className)}>
      {locPostcode && <div className="text-xsmall-end aui-small postcode">
        Postcode: {locPostcode}. {typeof onChangePostcode === "function" && <button type="button" className="aui-textlink" onClick={onChangePostcode}>Change?</button>}
      </div>}
      <h2 className="aui-headline-3 mb-4">Budget calculator<sup>*</sup></h2>
      
      {offer?.text && <div className="offer">
        <svg width="24" height="24">
          <path fill="currentColor" d="M0 0h10.9l12.4 12.4-10.8 10.8L0 10.7V0Zm1 1v9.3l11.5 11.5 9.4-9.4L10.5 1H1Zm1 3.5a2.5 2.5 0 1 1 5 0 2.5 2.5 0 0 1-5 0ZM4.5 3a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm5.76 4.03a1.75 1.75 0 1 1 2.48 2.48 1.75 1.75 0 0 1-2.48-2.48Zm1.77.7a.75.75 0 1 0-1.06 1.07.75.75 0 0 0 1.06-1.06ZM17.16 12H5.84v-1h11.32v1Zm-6.9 1.53a1.75 1.75 0 1 1 2.48 2.48 1.75 1.75 0 0 1-2.48-2.48Zm1.77.7a.75.75 0 1 0-1.06 1.07.75.75 0 0 0 1.06-1.06Z"/>
        </svg>
        <div>{offer.text}</div>
        {offer.link && <Button variant="text" href={offer.link} target="_blank" rel="noreferrer">See details</Button>}
      </div>}
      
      {typeof onCalculateBudget === "function" && <div className="budget row mb-4">
        <div className="col-12 col-large-6 mb-2">What is your desired weekly payment?</div>
        <div className="col-12 col-large-6">
          <div className="budget-input">
            <input
              type="text"
              value={weeklyBudget}
              onChange={(e)=>{
                setWeeklyBudget(formatNumber(e.target.value));
                debouncedGtmPush?.current("fbcBudgetInputLoaded", "FinanceBudgetCalculator", e.target.value);
              }}
            />
          </div>
        </div>
      </div>}

      {products?.length > 0 && <>
        <p className="mb-2">Select your loan type</p>
        <div className="mb-4 product">
          {products?.map(p => {
            if (p.Type === "Lease") { return null; }
            if (p.ID === "NovatedLeaseR") { return null; }
            if (p.ID === "AudiChoice") { return null; }
            return (
              <Button
                key={`productbtn${p.ID}`}
                variant={product === p.ID ? "primary" : "secondary"}
                theme={auiTheme}
                onClick={()=>{
                  setProduct(p.ID);
                  if (p.ID === "ConsumerLoan") { setRate(defaultConsumerRate); }
                  if (p.ID === "ChattelMortgage") { setRate(defaultBusinessRate); }
                  if (!hasTrackedProduct) { gtmPush("fbcLoanTypeUpdated", "FinanceBudgetCalculator"); setHasTrackedProduct(true); }
                }}
              >{p.ID === "ChattelMortgage" ? <>Chattel Mortgage<br />(Business Loan)</> : p.Label}</Button>
            );
          })}
        </div>
      </>}

      <p>Term</p>
      <RangeSlider
        min={12}
        max={60}
        step={12}
        value={term}
        labels={{12: "12 months", 60: "60 months"}}
        onChange={(v) => {
          setTerm(v);
        }}
        onChangeComplete={()=>{ gtmPush("fbcTermUpdated", "FinanceBudgetCalculator", term); }}
        handleLabel={term}
        theme={theme}
        className="mb-4"
      />
      
      <TextField
        name="deposit"
        label="Deposit"
        className="mb-4"
        theme={auiTheme}
        value={depositHasFocus ? deposit : formatDollar(deposit)}
        onFocus={() => { setDepositHasFocus(true); }}
        onBlur={() => { setDepositHasFocus(false); }}
        onChange={(v) => {
          setDeposit(unFormatNumber(v));
          debouncedGtmPush?.current("fbcDepositUpdated", "FinanceBudgetCalculator", v);
        }}
      />
      
      <p>{product === "ConsumerLoan" && <>Comparison interest</>}{product === "ChattelMortgage" && <>Interest</>} rate p.a.<sup>±</sup></p>
      <RangeSlider
        min={0} max={0.2} step={0.0001}
        value={rate}
        labels={{0: "0%", 0.2: "20%"}}
        handleLabel={`${1 * (rate * 100).toFixed(2)}%`}
        onChange={(v) => {
          setRate(parseFloat(v.toFixed(4)));
        }}
        onChangeComplete={()=>{ gtmPush("fbcInterestUpdated", "FinanceBudgetCalculator", rate); }}
        theme={theme}
        className="mb-4"
      />
      
      <p>Balloon<sup>&ne;</sup></p>
      <RangeSlider
        min={0}
        max={0.3}
        step={0.01}
        value={balloon}
        labels={{0: "0%", 0.3: "30%"}}
        onChange={(v) => { setBalloon(v); }}
        onChangeComplete={()=>{ gtmPush("fbcBalloonUpdated", "FinanceBudgetCalculator", balloon); }}
        handleLabel={`${(balloon * 100).toFixed(0)}%`}
        theme={theme}
        className="mb-5"
      />

      <p>
        <Button
          variant="text"
          theme={auiTheme}
          onClick={()=>{
            setProduct("ConsumerLoan");
            setRate(defaultConsumerRate);
            setDeposit(initStates.deposit);
            setTerm(initStates.term);
            setBalloon(initStates.balloon);
            setWeeklyBudget("");
            onReset();
            gtmPush("fbcStartoverClicked", "FinanceBudgetCalculator");
          }}
        >Clear filters and start over</Button>
      </p>
      
      <Button variant="text" theme={auiTheme} onClick={()=>{
        setShowDisclaimers(true);
        if (!hasTrackedDisclaimers) { gtmPush("fbcDisclaimerClicked", "FinanceBudgetCalculator"); setHasTrackedDisclaimers(true); }
      }}>Finance disclaimers</Button>
      
      <Modal isActive={showDisclaimers} variant="layer" closeButton close={()=>{ setShowDisclaimers(false); }}>
        <div className="pe-5 pt-5">
          <p className="mb-3">
            <b><sup>*</sup>Budget Calculator</b> - Finance available to approved applicants of Audi Financial Services (AFS) ABN 20 097 071 460, Australian Credit Licence Number 389344. AFS is a trading name of Volkswagen Financial Services Australia Pty Limited ABN 20 097 071 460. Standard credit assessment and lending criteria apply. The information provided by the budget calculator is indicative only. No result from the use of the calculator should be considered a loan application or an offer of a loan. All information provided by the budget calculator is of a general nature, based on the information you have provided and does not take into account your specific personal needs and financial circumstances. To check if this product is right for you, please see our website for our Target Market Determination document and get independent legal, financial or taxation advice. Nothing on this website constitutes or should be construed to constitute legal, taxation, or financial advice. Except for any statutory consumer guarantees which cannot be excluded, AFS gives no warranty in relation to your use of the AFS budget calculator or the accuracy of the information generated by the AFS budget calculator. The information provided by the budget calculator is published on behalf of AFS.
          </p>
          <div dangerouslySetInnerHTML={{__html: disclaimer}} />
          <p className="mb-3">
            <b><sup>&ne;</sup>Balloon Amount</b> - Additional interest charges accrue if a lump sum agreed at the time of the loan (“balloon payment”) is owed to AFS at the end of the loan term after all monthly repayments have been made.
          </p>
          <p className="mb-3">
            Audi Financial Services is a trading name of Volkswagen Financial Services Australia Pty Limited ABN 20 097 071 460, Australian Credit Licence Number 389344.
          </p>
          <p className="mb-3">
            *Please read the Target Market Determinations (TMD) document for our Financial Services products. This document ‘Target Market Determination’ describes who a product is appropriate for (target market), and any conditions around how the product can be distributed to customers. View the Target Market Determinations for our Financial Services Products <a href="https://www.audi.com.au/en/financial-services/target-market-determinations/" target="_blank" rel="noopener noreferrer" className="aui-textlink">here</a>.
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default BudgetCalculatorComponent;
